/**
 * Choosing a password, on Tab Service's own page.
 *
 * The link in an invitation or a Forgot password email is a Firebase action link. Followed
 * to Firebase's own handler it opens an unbranded Google page that says nothing about Tab
 * Service (TS-8285), so the API points the link here instead and this page does what that
 * handler does: check the code, take the new password, and open the session.
 *
 * It signs the person in itself rather than sending them to the sign-in page, which is what
 * the button has always promised. Sending them there was wrong twice over: they had just
 * proved they own the address and were asked to type the password they had chosen a second
 * later, and where the portal owns sign-in that page hands the browser to the portal, so an
 * invitation to e-filing ended at a login screen somewhere else.
 */
import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth-layout';
import { Button } from '@/components/ui/button';
import { Field, PasswordInput } from '@/components/ui/field';
import { ErrorNote, Spinner } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { authStore } from '@/lib/auth-store';
import { errorMessage } from '@/lib/api';
import { meQuery, isStaff } from '@/lib/queries';
import { LOGO_URL } from '@/lib/asset';

const searchSchema = z.object({
  /** Firebase's one-time code. Its own handler calls it oobCode, and so does the email. */
  oobCode: z.string().optional(),
  mode: z.string().optional(),
  continueUrl: z.string().optional(),
  lang: z.string().optional(),
});

export const Route = createFileRoute('/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

const formSchema = z
  .object({
    password: z.string().min(8, 'Use at least eight characters'),
    confirm: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'The two passwords are not the same',
  });

type FormValues = z.infer<typeof formSchema>;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { queryClient } = Route.useRouteContext();
  const { oobCode } = Route.useSearch();

  /** Whose account this link is for. Null until Firebase has said, or if it refused. */
  const [email, setEmail] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', confirm: '' },
  });

  // The code is checked before the form is offered, so an expired link says so at once
  // rather than after somebody has typed a password twice.
  useEffect(() => {
    let cancelled = false;
    if (!oobCode) {
      setLinkError('This address is missing the code from the email. Open the link in the email itself.');
      return;
    }
    void authStore
      .verifyPasswordReset(oobCode)
      .then((address) => {
        if (!cancelled) setEmail(address);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLinkError(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!oobCode) return;
    setFormError(null);
    try {
      await authStore.completePasswordReset(oobCode, values.password);
    } catch (error) {
      setFormError(errorMessage(error));
      return;
    }

    // The password is set from here on, so a failure below is never "that did not work".
    try {
      await authStore.signIn(email as string, values.password);
      queryClient.clear();
      const me = await queryClient.fetchQuery(meQuery());
      toast.success('Password set', 'You are signed in.');
      await navigate({ to: isStaff(me) ? '/admin' : '/' });
    } catch (error) {
      // Signing in failed, which is its own thing: the API may be unreachable, or this
      // address may have no e-filing account. Leave no half-session behind, say which of the
      // two happened, and let them sign in when it is working.
      await authStore.signOut().catch(() => {});
      queryClient.clear();
      toast.success('Password set', 'Sign in with your new password.');
      setFormError(errorMessage(error));
    }
  });

  const checking = !linkError && !email;

  return (
    <AuthLayout>
      <img src={LOGO_URL} alt="TAB Service Company" width={130} height={40} className="h-10 w-auto lg:hidden" />

      <div>
        <h1 className="text-[28px] leading-9 font-semibold text-primary">Set your password</h1>
        <p className="mt-2 text-base text-muted-foreground">
          {email
            ? `For ${email}. It is used to sign in to Tab Service from now on.`
            : 'This link was sent to you by Tab Service.'}
        </p>
      </div>

      {linkError ? (
        <>
          <ErrorNote title="This link cannot be used" detail={linkError} />
          <Button asChild variant="outline" size="lg" className="w-full">
            <Link to="/sign-in">Back to sign in</Link>
          </Button>
        </>
      ) : checking ? (
        <div className="flex items-center gap-3 text-base text-muted-foreground">
          <Spinner />
          Checking the link.
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field label="New password" error={form.formState.errors.password?.message}>
            {(props) => (
              <PasswordInput {...props} {...form.register('password')} autoComplete="new-password" autoFocus />
            )}
          </Field>

          <Field label="New password again" error={form.formState.errors.confirm?.message}>
            {(props) => <PasswordInput {...props} {...form.register('confirm')} autoComplete="new-password" />}
          </Field>

          {formError ? (
            <ErrorNote title="Could not sign you in" detail={formError} onDismiss={() => setFormError(null)} />
          ) : null}

          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Spinner /> : null}
            Set password and sign in
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
